import { useCallback } from 'react';

import type {
  OnSubmitCommentFn,
  OnSubmitCommentOptions,
  OnSubmitCommentReturn,
} from 'src/components';

import type { ActivityFeedHandle } from './ActivityFeed';
import type { OnSubmitAiFn } from './use-start-ai-thread';

interface ScrollFeedOnSubmit {
  handleSubmitComment: OnSubmitCommentFn;
  handleSubmitAI?: OnSubmitAiFn;
}

/**
 * After the reader posts, ask the feed to show the newest row — regardless of where they were
 * scrolled (the at-end gate correctly ignores background updates).
 */
export function useScrollFeedOnSubmit(
  feedRef: React.RefObject<ActivityFeedHandle | null>,
  {
    onSubmitAI,
    onSubmitComment,
  }: {
    onSubmitComment: OnSubmitCommentFn;
    onSubmitAI?: OnSubmitAiFn;
  }
): ScrollFeedOnSubmit {
  const handleSubmitComment = useCallback(
    async (markdown: string, options?: OnSubmitCommentOptions): Promise<OnSubmitCommentReturn> => {
      const comment = await onSubmitComment(markdown, options);
      if (comment) feedRef.current?.scrollToBottom();
      return comment;
    },
    [feedRef, onSubmitComment]
  );

  const handleSubmitAI = useCallback(
    async (...args: Parameters<OnSubmitAiFn>) => {
      if (!onSubmitAI) return false;
      const started = await onSubmitAI(...args);
      if (started) feedRef.current?.scrollToBottom();
      return started;
    },
    [feedRef, onSubmitAI]
  );

  return {
    handleSubmitAI: onSubmitAI ? handleSubmitAI : undefined,
    handleSubmitComment,
  };
}
